import React from "react";
import { Progress, Typography, Tooltip, Space, Divider } from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
  StarOutlined,
  VideoCameraOutlined,
  TrophyOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { CHART_COLORS } from "../../../../StatsAndAnalytics/utils/chartColors";
import "./FunnelProgressBars.scss";

const { Text } = Typography;

// Filter types for navigation
export type FunnelFilterType =
  | { type: "profileMatchStatus"; value: "qualified" | "unqualified" | "NA" }
  | { type: "roundStatus"; value: "qualified" | "unqualified" | "pending" }
  | { type: "status"; value: "shortlisted" | "interview" | "hired" }
  | { type: "all" };

// Application statistics data structure
export interface ApplicationStats {
  totalApplied: number;
  profileMatch: {
    qualified: number;
    unqualified: number;
    pending: number;
  };
  profileMatchPercent: {
    qualified: number;
    unqualified: number;
    pending: number;
  };
  aiInterviews: {
    qualified: number;
    unqualified: number;
    pending: number;
  };
  aiInterviewsPercent: {
    qualified: number;
    unqualified: number;
    pending: number;
  };
  shortlisted: number;
  shortlistedPercent: number;
  interview: number;
  interviewPercent: number;
  hired: number;
  hiredPercent: number;
}

interface FunnelProgressBarsProps {
  data: ApplicationStats | null;
  themeColor?: string;
  onFilterClick?: (filter: FunnelFilterType) => void;
}

interface StageRowProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  percent: number;
  color: string;
  total: number;
  onClick?: () => void;
}

// Simple progress bar stage row
const StageRow: React.FC<StageRowProps> = ({
  icon,
  label,
  count,
  percent,
  color,
  total,
  onClick,
}) => {
  const isClickable = count > 0;

  return (
    <div
      className={`funnel-stage-row ${isClickable ? "clickable" : ""}`}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="stage-header">
        <Space size={4}>
          <span className="stage-icon" style={{ color }}>
            {icon}
          </span>
          <Text className="stage-label">{label}</Text>
        </Space>
        <Space size={4}>
          <Text className="stage-count">{count}</Text>
          <Text className="stage-percent">({percent.toFixed(0)}%)</Text>
          {isClickable && <RightOutlined className="stage-arrow" />}
        </Space>
      </div>
      <Progress
        percent={total > 0 ? (count / total) * 100 : 0}
        strokeColor={color}
        showInfo={false}
        size="small"
        className="stage-progress"
      />
    </div>
  );
};

interface StackedStageRowProps {
  icon: React.ReactNode;
  label: string;
  qualified: number;
  unqualified: number;
  pending: number;
  qualifiedPercent: number;
  unqualifiedPercent: number;
  pendingPercent: number;
  total: number;
  onQualifiedClick?: () => void;
  onUnqualifiedClick?: () => void;
  onPendingClick?: () => void;
}

// Stacked progress bar for profile match and AI assessment
const StackedStageRow: React.FC<StackedStageRowProps> = ({
  icon,
  label,
  qualified,
  unqualified,
  pending,
  qualifiedPercent,
  unqualifiedPercent,
  pendingPercent,
  total,
  onQualifiedClick,
  onUnqualifiedClick,
  onPendingClick,
}) => {
  const totalCount = qualified + unqualified + pending;
  const qualifiedWidth = total > 0 ? (qualified / total) * 100 : 0;
  const unqualifiedWidth = total > 0 ? (unqualified / total) * 100 : 0;
  const pendingWidth = total > 0 ? (pending / total) * 100 : 0;

  return (
    <div className="funnel-stage-row stacked">
      <div className="stage-header">
        <Space size={4}>
          <span
            className="stage-icon"
            style={{ color: CHART_COLORS.success }}
          >
            {icon}
          </span>
          <Text className="stage-label">{label}</Text>
        </Space>
        <Text className="stage-count">{totalCount}</Text>
      </div>

      {/* Stacked progress bar */}
      <div className="stacked-progress-container">
        <Tooltip title={`Qualified: ${qualified} (${qualifiedPercent.toFixed(0)}%)`}>
          <div
            className={`stacked-progress-segment ${qualified > 0 ? "clickable" : ""}`}
            style={{
              width: `${qualifiedWidth}%`,
              backgroundColor: CHART_COLORS.success,
            }}
            onClick={qualified > 0 ? onQualifiedClick : undefined}
            role={qualified > 0 ? "button" : undefined}
          />
        </Tooltip>
        <Tooltip title={`Unqualified: ${unqualified} (${unqualifiedPercent.toFixed(0)}%)`}>
          <div
            className={`stacked-progress-segment ${unqualified > 0 ? "clickable" : ""}`}
            style={{
              width: `${unqualifiedWidth}%`,
              backgroundColor: CHART_COLORS.error,
            }}
            onClick={unqualified > 0 ? onUnqualifiedClick : undefined}
            role={unqualified > 0 ? "button" : undefined}
          />
        </Tooltip>
        <Tooltip title={`Pending: ${pending} (${pendingPercent.toFixed(0)}%)`}>
          <div
            className={`stacked-progress-segment ${pending > 0 ? "clickable" : ""}`}
            style={{
              width: `${pendingWidth}%`,
              backgroundColor: CHART_COLORS.placeholder,
            }}
            onClick={pending > 0 ? onPendingClick : undefined}
            role={pending > 0 ? "button" : undefined}
          />
        </Tooltip>
      </div>

      {/* Legend */}
      <div className="stacked-legend">
        <Space size={8} wrap>
          <span
            className={`legend-item ${qualified > 0 ? "clickable" : ""}`}
            onClick={qualified > 0 ? onQualifiedClick : undefined}
            role={qualified > 0 ? "button" : undefined}
          >
            <span
              className="legend-dot"
              style={{ backgroundColor: CHART_COLORS.success }}
            />
            <Text className="legend-text">Qualified: {qualified}</Text>
            {qualified > 0 && <RightOutlined className="legend-arrow" />}
          </span>
          <span
            className={`legend-item ${unqualified > 0 ? "clickable" : ""}`}
            onClick={unqualified > 0 ? onUnqualifiedClick : undefined}
            role={unqualified > 0 ? "button" : undefined}
          >
            <span
              className="legend-dot"
              style={{ backgroundColor: CHART_COLORS.error }}
            />
            <Text className="legend-text">Unqualified: {unqualified}</Text>
            {unqualified > 0 && <RightOutlined className="legend-arrow" />}
          </span>
          <span
            className={`legend-item ${pending > 0 ? "clickable" : ""}`}
            onClick={pending > 0 ? onPendingClick : undefined}
            role={pending > 0 ? "button" : undefined}
          >
            <span
              className="legend-dot"
              style={{ backgroundColor: CHART_COLORS.placeholder }}
            />
            <Text className="legend-text">Pending: {pending}</Text>
            {pending > 0 && <RightOutlined className="legend-arrow" />}
          </span>
        </Space>
      </div>
    </div>
  );
};

const FunnelProgressBars: React.FC<FunnelProgressBarsProps> = ({
  data,
  themeColor,
  onFilterClick,
}) => {
  if (!data) return null;

  const { totalApplied } = data;

  if (totalApplied === 0) {
    return (
      <div className="funnel-progress-bars empty-state">
        <Text type="secondary">No applications yet</Text>
      </div>
    );
  }

  // Navigation handlers
  const handleTotalClick = () => onFilterClick?.({ type: "all" });
  const handleProfileMatchClick = (status: "qualified" | "unqualified" | "NA") =>
    onFilterClick?.({ type: "profileMatchStatus", value: status });
  const handleRoundStatusClick = (status: "qualified" | "unqualified" | "pending") =>
    onFilterClick?.({ type: "roundStatus", value: status });
  const handleStatusClick = (status: "shortlisted" | "interview" | "hired") =>
    onFilterClick?.({ type: "status", value: status });

  return (
    <div className="funnel-progress-bars">
      {/* Total Applied */}
      <div
        className={`funnel-stage-row total-applied ${totalApplied > 0 ? "clickable" : ""}`}
        onClick={totalApplied > 0 ? handleTotalClick : undefined}
        role={totalApplied > 0 ? "button" : undefined}
        tabIndex={totalApplied > 0 ? 0 : undefined}
      >
        <div className="stage-header">
          <Space size={4}>
            <span
              className="stage-icon"
              style={{ color: themeColor || CHART_COLORS.primary }}
            >
              <UserOutlined />
            </span>
            <Text className="stage-label" strong>
              Total Applied
            </Text>
          </Space>
          <Space size={4}>
            <Text className="stage-count">{totalApplied}</Text>
            {totalApplied > 0 && <RightOutlined className="stage-arrow" />}
          </Space>
        </div>
        <Progress
          percent={100}
          strokeColor={themeColor || CHART_COLORS.primary}
          showInfo={false}
          size="small"
          className="stage-progress"
        />
      </div>

      <Divider className="funnel-divider" />

      {/* Profile Matching */}
      <StackedStageRow
        icon={<CheckCircleOutlined />}
        label="Profile Matching"
        qualified={data.profileMatch.qualified}
        unqualified={data.profileMatch.unqualified}
        pending={data.profileMatch.pending}
        qualifiedPercent={data.profileMatchPercent.qualified}
        unqualifiedPercent={data.profileMatchPercent.unqualified}
        pendingPercent={data.profileMatchPercent.pending}
        total={totalApplied}
        onQualifiedClick={() => handleProfileMatchClick("qualified")}
        onUnqualifiedClick={() => handleProfileMatchClick("unqualified")}
        onPendingClick={() => handleProfileMatchClick("NA")}
      />

      {/* AI Assessment */}
      <StackedStageRow
        icon={<ExperimentOutlined />}
        label="AI Assessment"
        qualified={data.aiInterviews.qualified}
        unqualified={data.aiInterviews.unqualified}
        pending={data.aiInterviews.pending}
        qualifiedPercent={data.aiInterviewsPercent.qualified}
        unqualifiedPercent={data.aiInterviewsPercent.unqualified}
        pendingPercent={data.aiInterviewsPercent.pending}
        total={totalApplied}
        onQualifiedClick={() => handleRoundStatusClick("qualified")}
        onUnqualifiedClick={() => handleRoundStatusClick("unqualified")}
        onPendingClick={() => handleRoundStatusClick("pending")}
      />

      <Divider className="funnel-divider" />

      {/* Shortlisted */}
      <StageRow
        icon={<StarOutlined />}
        label="Shortlisted"
        count={data.shortlisted}
        percent={data.shortlistedPercent}
        color={CHART_COLORS.purple}
        total={totalApplied}
        onClick={() => handleStatusClick("shortlisted")}
      />

      {/* Interview */}
      <StageRow
        icon={<VideoCameraOutlined />}
        label="Interview"
        count={data.interview}
        percent={data.interviewPercent}
        color={CHART_COLORS.interview}
        total={totalApplied}
        onClick={() => handleStatusClick("interview")}
      />

      {/* Hired */}
      <StageRow
        icon={<TrophyOutlined />}
        label="Hired"
        count={data.hired}
        percent={data.hiredPercent}
        color={CHART_COLORS.hired}
        total={totalApplied}
        onClick={() => handleStatusClick("hired")}
      />
    </div>
  );
};

export default FunnelProgressBars;
